import { useEffect, useState } from "react";
import { resolveListingImages } from "@/lib/categoryImages";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MapPin, Clock, ArrowRight, AlertCircle, Recycle, Cloud, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, floorLabel } from "@/lib/api";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";
import PublicHeader from "@/components/PublicHeader";

export default function ReservationSuccess() {
  const { unit } = useUnit();
  const { id } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    api
      .get(`/reservations/${id}?token=${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, token]);

  const isExpired = data?.reservation?.status === "expired";

  const impact = data?.impact;
  async function shareImpact() {
    const kg = impact?.weight_kg ?? 0;
    const co2 = impact?.co2e_kg ?? 0;
    const text = `I just rescued ${kg}kg of flooring from landfill with Floor-Match — saving an estimated ${co2}kg of CO₂e. Find surplus flooring that fits your room:`;
    const url = typeof window !== "undefined" ? window.location.origin : "";
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Floor-Match impact", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Impact copied — paste it anywhere to share");
      }
    } catch (e) {
      /* user dismissed the share sheet — no-op */
    }
  }

  return (
    <>
      <PublicHeader />
      <main className="min-h-screen" data-testid="reservation-success-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          {loading && <div>Loading…</div>}
          {!loading && notFound && (
            <div
              className="bg-white border border-slate-200 rounded-2xl p-8 text-center"
              data-testid="reservation-not-found"
            >
              <h1 className="font-display font-bold text-2xl text-slate-900">
                We can't find that reservation
              </h1>
              <p className="text-slate-600 mt-2">
                Reservation links are private and expire once opened. Please use the confirmation
                link from your original booking, or reserve again.
              </p>
              <Link to="/search" className="fm-btn-primary inline-block mt-5" data-testid="back-to-search">
                Back to search
              </Link>
            </div>
          )}
          {data && (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className={`${isExpired ? "bg-slate-700" : "bg-[#2A5A3B]"} text-white p-6 sm:p-8`}>
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-white/15">
                    {isExpired ? (
                      <AlertCircle className="w-6 h-6" aria-hidden="true" />
                    ) : (
                      <CheckCircle2 className="w-6 h-6" aria-hidden="true" />
                    )}
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-semibold opacity-80">
                      {isExpired ? "Reservation expired" : "Reservation confirmed"}
                    </div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl mt-0.5">
                      {isExpired
                        ? "Your collection window has closed."
                        : "Your remnant is being held."}
                    </h1>
                  </div>
                </div>
                <div className="mt-4 text-sm opacity-90">
                  Reference{" "}
                  <span className="font-mono bg-white/15 rounded px-2 py-0.5" data-testid="reservation-ref">
                    {data.reservation.id.slice(0, 8).toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="p-6 sm:p-8 space-y-6">
                {isExpired && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="expired-notice">
                    <strong>This reservation has expired</strong> and the remnant has been
                    returned to the marketplace. If you'd still like it, please browse and reserve
                    again — it may still be available.
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <InfoRow
                    icon={<Clock className="w-4 h-4" />}
                    label="Collection window"
                    value={new Date(data.reservation.collection_window_ends_at).toLocaleString("en-GB", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  />
                  <InfoRow
                    icon={<MapPin className="w-4 h-4" />}
                    label="Collect from"
                    value={`${data.retailer?.name || ""}${
                      data.retailer?.collection_postcode
                        ? " · " + data.retailer.collection_postcode
                        : ""
                    }`}
                  />
                </div>

                <div className="bg-[#2A5A3B]/5 border border-[#2A5A3B]/20 rounded-xl p-4 text-sm text-slate-700" data-testid="platform-contact-note">
                  <div className="font-semibold text-slate-900 mb-1">Retailer & collection details revealed</div>
                  Your order is confirmed. {data.retailer?.name || "The retailer"} will contact you
                  shortly through FloorMatch to arrange a pick-up time. Please reply through the
                  platform — direct phone numbers stay private on both sides.
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                  <div className="flex items-start gap-4">
                    {resolveListingImages(data.listing).photos[0] && (
                      <img
                        src={resolveListingImages(data.listing).photos[0]}
                        alt=""
                        className="w-24 h-24 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <div className="font-display font-semibold text-slate-900 capitalize">
                        {data.listing?.colour} {floorLabel(data.listing?.flooring_type)}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">
                        {dimPairLabel(data.listing?.width_m, data.listing?.length_m, unit)} ·{" "}
                        {(data.listing?.width_m * data.listing?.length_m).toFixed(2)}m²
                      </div>
                      <div className="text-sm text-slate-600">
                        Ref {data.reservation.listing_reference}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-xl text-slate-900">
                        {gbp(data.reservation.price_gbp)}
                      </div>
                      <div className="text-xs text-slate-500">to pay on collection</div>
                    </div>
                  </div>
                </div>

                {data.reservation.fitting_interest && (
                  <div className="bg-[#E05915]/10 border border-[#E05915]/20 rounded-xl p-4 text-sm text-slate-800" data-testid="fitter-status">
                    <strong>Your fitting job has been sent to local fitters.</strong> Approved fitters
                    near you have been notified — the first to accept will be in touch through
                    FloorMatch to arrange a date and price. We'll email you as soon as one accepts.
                  </div>
                )}

                {data.reservation.delivery_requested && (
                  <div className="bg-[#2A5A3B]/10 border border-[#2A5A3B]/20 rounded-xl p-4 text-sm text-slate-800" data-testid="delivery-status">
                    <strong>Your collection &amp; delivery request has been sent to local drivers.</strong>{" "}
                    {data.reservation.delivery_fee_gbp != null
                      ? `The delivery fee of ${gbp(data.reservation.delivery_fee_gbp)} is paid directly to the driver, not in this payment.`
                      : "The delivery fee is paid directly to the driver, not in this payment."}
                  </div>
                )}

                {!isExpired && impact && impact.weight_kg > 0 && (
                  <div
                    className="relative overflow-hidden rounded-xl bg-[#0F3D2E] text-white p-5 sm:p-6"
                    data-testid="impact-receipt"
                  >
                    <div
                      className="absolute inset-0 opacity-40"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 85% 20%, rgba(42,90,59,0.7), transparent 45%)",
                      }}
                      aria-hidden="true"
                    />
                    <div className="relative">
                      <div className="flex items-center gap-2 text-[#9fe0b6]">
                        <Recycle className="w-4 h-4" aria-hidden="true" />
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          Your green impact
                        </span>
                      </div>
                      <p className="text-white/90 text-sm mt-2 max-w-md">
                        By reusing this flooring instead of buying new, you kept it out of landfill.
                        Nice one.
                      </p>
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-sm">
                          <div className="font-display font-bold text-3xl leading-none">
                            {impact.weight_kg}
                            <span className="text-base font-semibold text-[#9fe0b6]"> kg</span>
                          </div>
                          <div className="text-xs text-white/80 mt-1.5">saved from landfill</div>
                        </div>
                        <div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-sm">
                          <div className="font-display font-bold text-3xl leading-none flex items-baseline gap-1">
                            {impact.co2e_kg}
                            <span className="text-base font-semibold text-[#9fe0b6]">kg</span>
                          </div>
                          <div className="text-xs text-white/80 mt-1.5 flex items-center gap-1">
                            <Cloud className="w-3 h-3" aria-hidden="true" /> CO₂e avoided (est.)
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={shareImpact}
                        className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white text-[#0F3D2E] font-semibold text-sm hover:bg-[#eafff1] transition-colors"
                        data-testid="share-impact-btn"
                      >
                        <Share2 className="w-4 h-4" aria-hidden="true" /> Share my impact
                      </button>
                      <p className="text-[11px] text-white/50 mt-3">
                        Figures are estimates based on typical flooring densities.
                      </p>
                    </div>
                  </div>
                )}

                <div className="pt-4 flex flex-wrap gap-3">
                  <Link
                    to="/search"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:text-[#E05915] transition-colors"
                    data-testid="continue-browsing"
                  >
                    Continue browsing <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
